import { MenuItem } from 'primeng/api';
import { ExtendedMenuItem } from '../menu.types';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';

/**
 * Relationship Manager Menu Configuration
 *
 * Returns the menu items for Relationship Manager role users.
 * Separated from main menu component for better maintainability.
 *
 * @param logoutCallback - Function to call when user clicks logout
 * @param askBettaAlphaCallback - Function to call when user clicks "Ask BettaAlpha"
 * @returns MenuItem object with nested items for relationship manager role
 */
export function getRelationshipManagerMenuItems(logoutCallback: () => void, askBettaAlphaCallback: () => void): MenuItem {
    return {
        label: 'Home',
        items: [
            {
                label: 'Dashboard',
                iconPath: '/icons/dashboard.svg',
                routerLink: ['/rm/dashboard']
            },

            ...(AI_FEATURE_ENABLED ? [{
                label: 'Ask BettaAlpha',
                iconPath: '/icons/ai-sparkle.svg',
                command: askBettaAlphaCallback
            } as ExtendedMenuItem] : []),
            {
                label: 'Opportunities',
                iconPath: '/icons/deals.svg',
                items: [
                    {
                        label: 'All Opportunities',
                        routerLink: ['/rm/deal-management/deals']
                    },
                    {
                        label: 'Live',
                        routerLink: ['/rm/deal-management/deals/live']
                    },
                    {
                        label: 'Closing',
                        routerLink: ['/rm/deal-management/deals/closing']
                    },
                    {
                        label: 'Completed',
                        routerLink: ['/rm/deal-management/deals/completed']
                    },
                    {
                        label: 'Missed',
                        routerLink: ['/rm/deal-management/deals/missed']
                    }
                ]
            } as ExtendedMenuItem,
            {
                label: 'Shared Opportunities',
                iconPath: '/icons/shared.svg',
                menuType: 'box-submenu',
                expanded: false,
                boxSections: [
                    {
                        title: 'Shared with Clients',
                        icon: '/icons/clients.svg',
                        items: [
                            {
                                label: 'All Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client']
                            },
                            {
                                label: 'Live Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/live']
                            },
                            {
                                label: 'Expressed Interest',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/expressed-interest']
                            },
                            {
                                label: 'Closing Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/closing']
                            },
                            {
                                label: 'Completed Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/completed']
                            },
                            {
                                label: 'Declined Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/declined']
                            },
                            {
                                label: 'Missed Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/missed']
                            }
                            ,
                            {
                                label: 'Withdrawn Opportunities',
                                routerLink: ['/rm/deal-management/shared-opportunities/client/withdrawn']
                            }
                        ]
                    }
                ]
            } as ExtendedMenuItem,
            {
                label: 'Users',
                iconPath: '/icons/users.svg',
                items: [
                    {
                        label: 'Clients',
                        routerLink: ['/rm/clients']
                    }
                ]
            } as ExtendedMenuItem,
            {
                label: 'Tasks',
                iconPath: '/icons/book.svg',
                menuType: 'box-submenu',
                expanded: false,
                defaultCollapsed: true,
                boxSections: [
                    {
                        title: 'Deals',
                        items: [
                            { label: 'Deal Approval', routerLink: ['/rm/new-tasks/deals/deal-approval'] },
                            // RM: Deal Subscription and Deal Other hidden — RM deal tasks use Deal Approval only.
                            // { label: 'Deal Subscription', routerLink: ['/rm/new-tasks/deals/deal-subscription'] },
                            // { label: 'Deal Other', routerLink: ['/rm/new-tasks/deals/deal-other'] },
                            { label: 'History', routerLink: ['/rm/new-tasks/deals/history'] },
                        ],
                    },
                    // Users tasks hidden for now across portals.
                    // {
                    //     title: 'Users',
                    //     items: [
                    //         { label: 'Users', routerLink: ['/rm/new-tasks/users/users'] },
                    //         { label: 'History', routerLink: ['/rm/new-tasks/users/history'] },
                    //     ],
                    // },
                ],
            } as ExtendedMenuItem,

            {
                label: 'Notifications',
                iconPath: '/icons/bell-outlined.svg',
                routerLink: ['/notifications']
            } as ExtendedMenuItem,
            {
                label: 'Questions',
                iconPath: '/icons/question-answer-file.svg',
                routerLink: ['/questions']
            } as ExtendedMenuItem,
            {
                label: 'Feedback',
                iconPath: '/icons/message.svg',
                routerLink: ['/feedback']
            } as ExtendedMenuItem,
            {
                label: 'Learning Hub',
                iconPath: '/icons/book.svg',
                menuType: 'submenu',
                items: [{ label: 'Content', routerLink: ['/rm/learning-hub'] }],
            } as ExtendedMenuItem,
            {
                label: 'My Profile',
                iconPath: '/icons/user.svg',
                routerLink: ['/user-profile']
            } as ExtendedMenuItem,
            {
                label: 'Logout',
                iconPath: '/icons/log-out.svg',
                command: logoutCallback
            } as ExtendedMenuItem
        ]
    };
}
